import { Alert, Anchor, Box, Breadcrumbs, Grid, Text } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../../components/ErrorMessage';
import Metadata from '../../../components/Metadata';
import RecommenderForm from '../../../components/Recommenders/RecommenderForm';
import Spinner from '../../../components/Spinner';
import useRestorePlacementSession from '../../../components/useRestorePlacementSession';
import { Page } from '../../../constants/pageTitles';
import useFetch from '../../../helpers/useFetch';

const page = `${Page['recommenders']}`;

const RecommendersDetailsPage = () => {
  const { accountId, domainId } = useParams();
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const { pageId, id } = useParams();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [recommender, setRecommender] = useState();
  const recommenderPath = `/api/admin/accounts/${accountId}/domains/${domainId}/pages/${pageId}/recommenders/${id}`;

  // Coming back from positioning the block on the customer's own site: the
  // unsaved form the merchant left with is on the session, and the position
  // they picked is laid over it. Read here rather than in the form so the form
  // is built from the restored block instead of being rewritten after mount —
  // this page's spinner covers the read, so there is never an editable,
  // saveable form standing on the pre-trip block.
  const placement = useRestorePlacementSession({ blockPath: recommenderPath });

  useEffect(() => {
    fetch(recommenderPath)
      .then(({ recommender }) => {
        if (!recommender) {
          navigate(`/accounts/${accountId}/domains/${domainId}/pages?page=${pageId}&type=recommenders`);
        }

        setRecommender(recommender);
      })
      .catch(setLoadError);
  }, [fetch, navigate, accountId, domainId, pageId, recommenderPath]);

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (recommender === undefined || placement.pending) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  const del = (cb) => {
    fetch(recommenderPath, {
      method: 'DELETE',
    })
      .then(() => {
        if (typeof cb === 'function') {
          cb();
        }
        navigate(
          `/accounts/${accountId}/domains/${domainId}/pages?type=recommenders&page=${recommender?.pageId || ''}`
        );
      })
      .catch(setUserError);
  };

  const enitityLabel = recommender.name;
  return (
    <>
      <Metadata title={`${page} | ${enitityLabel} | Edit`} />
      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Anchor
          href={`#/accounts/${accountId}/domains/${domainId}/pages?type=recommenders&page=${recommender?.pageId || ''}`}
        >
          {recommender.page.name} Product Blocks
        </Anchor>
        <Anchor href={`#${location.pathname.replace('details', 'analytics')}`}>{enitityLabel}</Anchor>
        <Text href={null}>Edit</Text>
      </Breadcrumbs>
      {placement.notice && (
        <Alert color="blue" icon={<IconInfoCircle size={16} />} mb="md">
          <Text size="xs">{placement.notice}</Text>
        </Alert>
      )}
      {placement.error && (
        // Closable, unlike the notice: saving is the answer to "press Save to
        // keep it", but there is nothing to save in response to a session that
        // could not be read, so otherwise this would sit here for the visit.
        // `dismiss` rather than `onSaved` — closing the message must not cost
        // the merchant the reload the message just offered them.
        <Alert
          color="orange"
          icon={<IconAlertTriangle size={16} />}
          mb="md"
          withCloseButton
          closeButtonLabel="Dismiss"
          onClose={placement.dismiss}
        >
          <Text size="xs">{placement.error}</Text>
        </Alert>
      )}
      <Grid>
        <Grid.Col span={12}>
          <RecommenderForm
            scope="web"
            // The restored block when the merchant came back from a positioning
            // trip, the saved one otherwise. Whichever it is, the form is
            // mounted on it once and nothing replaces it afterwards.
            recommender={placement.restored || recommender}
            initialTab={placement.restored ? 'displayConfiguration' : undefined}
            // "press Save to keep it" stops applying once they have — and a
            // save is also what makes a stored snapshot stale, so this is
            // where the session token is retired if the read never succeeded.
            onSaved={placement.onSaved}
            deleteRecommender={(cb) => del(cb)}
            cancel={() =>
              navigate(
                `/accounts/${accountId}/domains/${domainId}/pages?type=recommenders&page=${recommender?.pageId || ''}`
              )
            }
            allowSegmentSelection={true}
          />
        </Grid.Col>
      </Grid>
      {userError ? (
        <Box ta="right">
          <ErrorMessage message={userError.message} />
        </Box>
      ) : null}
    </>
  );
};

export default RecommendersDetailsPage;
